import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AvisosIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useNotification } from '../hooks/useNotification.js'
import { Aviso, destinoDoAviso, marcarAvisosLidos, useAvisos } from './avisos'
import { dataHora } from './solicitacoes/comum'
import './solicitacoes/solicitacoes.scss'

interface Props {
  /** "menu" fica no rodapé do menu lateral; "topo" na barra do celular. */
  variante: 'menu' | 'topo'
  compacta?: boolean
}

/** O sino do layout novo: avisos não lidos, com o número na etiqueta. Cada aviso leva ao que mudou. */
export default function Sino({ variante, compacta }: Props) {
  const { avisos, atualizar } = useAvisos()
  const { notify } = useNotification()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState<React.CSSProperties>({})
  const botao = useRef<HTMLButtonElement>(null)
  const painel = useRef<HTMLDivElement>(null)

  useEffect(() => { setAberto(false) }, [pathname])

  useEffect(() => {
    if (!aberto) return
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node
      if (!painel.current?.contains(alvo) && !botao.current?.contains(alvo)) setAberto(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false) }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc) }
  }, [aberto])

  const alternar = () => {
    if (!aberto && botao.current) {
      const r = botao.current.getBoundingClientRect()
      setPosicao(variante === 'menu'
        ? { left: Math.min(r.right + 8, Math.max(12, window.innerWidth - 372)), bottom: Math.max(12, window.innerHeight - r.bottom) }
        : { top: r.bottom + 8, right: 12 })
      atualizar()
    }
    setAberto(!aberto)
  }

  const abrir = async (a: Aviso) => {
    setAberto(false)
    const erro = await marcarAvisosLidos([a.id])
    if (erro) notify(erro, { variant: 'error' })
    const destino = destinoDoAviso(a)
    if (destino) navigate(destino)
  }

  const marcarTodos = async () => {
    const erro = await marcarAvisosLidos(avisos.map(a => a.id))
    if (erro) notify(erro, { variant: 'error' })
  }

  const n = avisos.length
  const rotulo = n ? `Avisos, ${n} não ${n === 1 ? 'lido' : 'lidos'}` : 'Avisos'

  return (
    <div className="np-sino">
      <button ref={botao} type="button" className={variante === 'menu' ? 'np-nav' : 'np-ico'} onClick={alternar}
              aria-label={rotulo} aria-expanded={aberto} title={compacta ? 'Avisos' : undefined}>
        <AvisosIcon />
        {variante === 'menu' && <span>Avisos</span>}
        {n > 0 && <i className="np-sino-n" aria-hidden="true">{n > 99 ? '99+' : n}</i>}
      </button>
      {aberto && (
        <div ref={painel} className="np-sino-painel" style={posicao} role="dialog" aria-label="Avisos">
          <div className="np-sino-cab">
            <b>Avisos</b>
            {n > 0 && <button type="button" onClick={marcarTodos}>Marcar todos como lidos</button>}
          </div>
          {n === 0 ? <div className="np-sino-vazio">Nenhum aviso novo.</div> : (
            <ul className="np-sino-lista">
              {avisos.map(a => (
                <li key={a.id}>
                  <button type="button" className="np-sino-item" onClick={() => abrir(a)}>
                    <span>{a.message}</span><small>{dataHora(a.createdAt)}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
